'use client';

/**
 * Floating "back to top" button, rendered once at the shell root by
 * {@code SiteShellPresenter}. Hidden until the visitor scrolls past a
 * threshold, then fades in at a configurable corner; clicking it
 * smooth-scrolls the document back to the top (honouring
 * {@code prefers-reduced-motion}).
 *
 * <p>Author-driven, no host data — configured from the admin Shell
 * editor's "Theme defaults" tab ({@code shell.theme.scrollToTop}).
 * The presenter only mounts this when {@code show === true}, so the
 * component assumes it's meant to be visible and just manages its own
 * reveal-on-scroll state.
 */
import { useCallback, useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { ArrowUp } from 'lucide-react';
import type { ScrollToTopPosition } from '../../portal';
import { useLocale } from '../../portal';

const RING_RADIUS = 20;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

export interface ScrollToTopButtonProps {
  /** Corner the button anchors to. Default {@code 'bottom-right'}. */
  position?: ScrollToTopPosition;
  /** Scroll distance (px) after which the button appears. Default 320. */
  threshold?: number;
}

/** Locale-aware accessible label. Falls back to English. */
function label(locale: string): string {
  const dict: Record<string, string> = { tr: 'Yukarı çık', en: 'Back to top' };
  return dict[locale] ?? dict['en']!;
}

/**
 * The element that actually scrolls. Most sites scroll the document
 * ({@code document.scrollingElement}), but some app shells give
 * {@code <body>} a fixed height + {@code overflow:auto} so the body is
 * the scroller and {@code window.scrollY} stays 0. Prefer whichever
 * element genuinely overflows.
 */
function findScroller(): HTMLElement {
  const doc = document.scrollingElement as HTMLElement | null;
  if (doc && doc.scrollHeight > doc.clientHeight + 1) return doc;
  if (document.body.scrollHeight > document.body.clientHeight + 1) return document.body;
  return doc ?? document.documentElement;
}

/** Horizontal anchoring per position: the CSS inset props plus the
 *  base (reveal-independent) transform the position needs. */
function anchor(position: ScrollToTopPosition): { inset: CSSProperties; baseTransform: string } {
  switch (position) {
    case 'bottom-left':
      return { inset: { left: 'clamp(16px, 3vw, 32px)' }, baseTransform: '' };
    case 'bottom-center':
      return { inset: { left: '50%' }, baseTransform: 'translateX(-50%)' };
    case 'bottom-right':
    default:
      return { inset: { right: 'clamp(16px, 3vw, 32px)' }, baseTransform: '' };
  }
}

export function ScrollToTopButton({
  position = 'bottom-right',
  threshold = 320,
}: ScrollToTopButtonProps) {
  const { locale } = useLocale();
  const [visible, setVisible] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    // rAF-throttled reveal check. We listen on window in the CAPTURE
    // phase so we catch scroll from BOTH the document and an inner
    // element scroller (scroll events don't bubble, but capture-phase
    // listeners on window still receive element scrolls). The scroll
    // offset is read from whichever element actually overflows — some
    // app shells scroll <body>, leaving window.scrollY pinned at 0.
    let ticking = false;
    const evaluate = () => {
      ticking = false;
      const scroller = findScroller();
      const y = scroller.scrollTop || window.scrollY || 0;
      const nowVisible = y > threshold;
      setVisible(nowVisible);
      // Once hidden the button goes pointer-events:none, so if the
      // cursor is still resting over its screen position no mouseleave
      // ever fires to clear the hover/active style — clear it here
      // instead, so it doesn't reappear already "active" next reveal.
      if (!nowVisible) setHovered(false);
      const max = scroller.scrollHeight - scroller.clientHeight;
      setProgress(max > 0 ? Math.min(1, Math.max(0, y / max)) : 0);
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(evaluate);
    };
    evaluate();
    window.addEventListener('scroll', onScroll, { passive: true, capture: true });
    return () =>
      window.removeEventListener('scroll', onScroll, {
        capture: true,
      } as EventListenerOptions);
  }, [threshold]);

  const scrollTop = useCallback(() => {
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    findScroller().scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
  }, []);

  const { inset, baseTransform } = anchor(position);
  const style: CSSProperties = {
    position: 'fixed',
    bottom: 'clamp(16px, 3vw, 32px)',
    ...inset,
    zIndex: 40,
    display: 'grid',
    placeItems: 'center',
    width: 44,
    height: 44,
    padding: 0,
    margin: 0,
    lineHeight: 0,
    borderRadius: 999,
    border: '1px solid var(--color-border-subtle, rgba(0,0,0,0.08))',
    background: hovered ? 'var(--color-brand-500, #4f46e5)' : 'var(--color-surface-card, #ffffff)',
    color: hovered ? 'var(--color-text-on-brand)' : 'var(--color-brand-500, #4f46e5)',
    boxShadow: hovered
      ? '0 10px 28px -6px var(--color-brand-500, #4f46e5)'
      : 'var(--shadow-lg, 0 8px 24px rgba(0,0,0,0.16))',
    cursor: 'pointer',
    // Fade + slide in, with a subtle scale/lift on hover for feedback;
    // pointer-events off while hidden so it never intercepts clicks on
    // content behind it.
    opacity: visible ? 1 : 0,
    transform:
      `${baseTransform} translateY(${visible ? (hovered ? '-3px' : '0') : '8px'}) scale(${hovered ? 1.06 : 1})`.trim(),
    pointerEvents: visible ? 'auto' : 'none',
    transition:
      'opacity 220ms ease, transform 200ms ease, background-color 200ms ease, color 200ms ease, box-shadow 200ms ease',
  };

  return (
    <button
      id="scroll-to-top-button"
      type="button"
      onClick={scrollTop}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      aria-label={label(locale)}
      title={label(locale)}
      aria-hidden={!visible}
      tabIndex={visible ? 0 : -1}
      style={style}
    >
      <svg
        width={44}
        height={44}
        viewBox="0 0 44 44"
        aria-hidden
        style={{ position: 'absolute', inset: 0, transform: 'rotate(-90deg)' }}
      >
        <circle
          cx={22}
          cy={22}
          r={RING_RADIUS}
          fill="none"
          stroke="var(--color-brand-500, #4f46e5)"
          strokeWidth={2}
          strokeLinecap="round"
          strokeDasharray={RING_CIRCUMFERENCE}
          strokeDashoffset={RING_CIRCUMFERENCE * (1 - progress)}
          style={{ transition: 'stroke-dashoffset 100ms linear' }}
        />
      </svg>
      <ArrowUp
        size={18}
        strokeWidth={2.25}
        aria-hidden
        style={{
          transition: 'transform 200ms ease',
          transform: hovered ? 'translateY(-1px)' : 'none',
        }}
      />
    </button>
  );
}
